import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import type { LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
} from "@remix-run/react";
import io from "socket.io-client";

import "~/tailwind.css";
import { User, isAuthenticated } from "./services/auth.server";
import NavBar from "./components/NavBar";
import { RootContext } from "./context/rootContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Socket } from "socket.io-client";
import { SocketProvider } from "./context/socketContext";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const user: User | null = await isAuthenticated(request);

  return json({
    user: user
      ? { username: user.username, twitchId: user.twitchId, profilePic: user.profilePic }
      : null,
    ENV: {
      BASE_URL: process.env.IMAGE_BASE_URL || "",
    },
  });
};

function App() {
  const { user, ENV } = useLoaderData<typeof loader>();
  const location = useLocation();
  const queryClient = new QueryClient();
  const [socket, setSocket] = useState<Socket>();

  if (location.pathname.startsWith("/overlay")) {
    useEffect(() => {
      const socket = io("/overlay", { autoConnect: false });
      setSocket(socket);

      return () => {
        socket.close();
      };
    }, []);

    useEffect(() => {
      if (!socket) return;

      socket.on("confirmation", () => {
        console.log("yippee");
      });
    }, [socket]);

    return (
      <html lang="en" className="h-full">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <Meta />
          <Links />
        </head>
        <body>
          <RootContext.Provider value={{ imageBaseUrl: ENV.BASE_URL }}>
            <QueryClientProvider client={queryClient}>
              <SocketProvider socket={socket}>
                <Outlet />
              </SocketProvider>
            </QueryClientProvider>
          </RootContext.Provider>
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    );
  }

  useEffect(() => {
    const socket = io("/notifications", { autoConnect: false });
    setSocket(socket);

    return () => {
      socket.close();
    };
  }, []);

  return (
    <html lang="en" className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="subpixel-antialiased bg-[#0C0C14] text-white">
        <RootContext.Provider value={{ imageBaseUrl: ENV.BASE_URL }}>
          <QueryClientProvider client={queryClient}>
            <SocketProvider socket={socket}>
              <NavBar user={user} />
            </SocketProvider>
            <main className="mt-0 min-w-0 px-2 py-4 font-main lg:mt-12 md:px-8 2xl:py-8">
              <Outlet />
            </main>
          </QueryClientProvider>
          <ScrollRestoration />
          <Scripts />
        </RootContext.Provider>
      </body>
    </html>
  );
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();

  console.error(error);
  captureRemixErrorBoundaryError(error);
  return (
    <html>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>Something went wrong | Gremgacha</title>
        <Meta />
        <Links />
      </head>
      <body>
        <h1>You really shouldn't be here</h1>
        <Scripts />
      </body>
    </html>
  );
}

